import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import styled from 'styled-components'
import type { VFC } from 'react'
import { Body, Bold } from '@panwds/react-ui'
import { ConnectedPaginator, ConnectedResultsSummary, ConnectedTable, ConnectedTableLayout, TableProvider } from '@panwds/react-table'
import SubscriptionService from '../services/SubscriptionService'
import { APP_ID_LOGGING_SERVICE } from './common/GlobalConstants'
import AppContext from '../../common/context/AppContext'
import { CircleCheckIcon, ExclamationCircleIcon } from '@panwds/icons'

const TableHeightFix = styled.div`
  > .tw-flex.tw-flex-col.tw-flex-grow,
  > .tw-flex.tw-flex-col.tw-flex-grow > .tw-flex.tw-overflow-auto {
    height: 100%;
  }
  div[role="row"] {
    height: auto !important;
  }
  [style='min-height: 289px;'] {
    min-height: unset !important;
  }
`
const SubscriptionRenderer = (value: any) => {

  const { getAppName } = useContext(AppContext)

  const renderedSubscriptions = useMemo(() => {
    //console.log('value', value)
    const subs = value.value
    if (!subs.length) {
      return <Body addClassName='tw-text-blue-600 tw-truncate tw-mr-1'>No Subscriptions</Body>
    }

    return subs?.map((each: any, index: number) => {
      return (
        <div className='tw-flex tw-flex-col tw-gap-1' key={index}>
          <div className='tw-flex tw-flex-col'>
            <Body addClassName='tw-text-blue-600 tw-truncate tw-mr-1'>
              <Bold>{getAppName(each.app_id)}</Bold>
            </Body>
          </div>
        </div>
      )
    })
  }, [getAppName, value.value])

  return (
    <div className="tw-relative tw-h-full tw-truncate cm-py-2">
      {renderedSubscriptions}
    </div>
  )
}
const getStatus = (value: string) => {
  switch (value) {
    case 'complete':
      return (
        <span className={'tw-flex tw-flex-row tw-items-center tw-gap-1 tw-h-full'}>
          <CircleCheckIcon size='sm' className='cm-text-green-400' />
          Complete
        </span>
      )
    case 'pending':
      return <>
        <span className={'tw-flex tw-flex-row tw-items-center tw-gap-1 tw-h-full'}>
          <ExclamationCircleIcon size='sm' className='cm-text-red-500' />
          Pending
        </span>
      </>
    default:
      return
  }
}
const getDateFormat = (dateValue: any) => {
  if (dateValue) {
    const [year, month, day] = dateValue.split('-')
    return `${month}/${day.slice(0, 2)}/${year}`
  }
  return ''
}
const DeploymentProfileNameRenderer = (row: any) => {
  // console.log('row', row)
  return (
    <div className='tw-flex tw-items-center tw-gap-0.5'>
      <Body addClassName='tw-text-blue-600 tw-truncate tw-mr-1'>
        <a className="tw-text-link dark:tw-text-dark-bg-link" href={`/activation/profile/${row.row.original.deployment_profile_id}`}>{row.row.values.profile_name}</a>
      </Body>
    </div>
  )
}
const DeploymentProfilesTable: VFC<any> = (props) => {
  const { auth, globalTsgId, selectedTreeNode, supportedRegionsByKey } = props
  const { user } = auth
  const [deploymentProfiles, setDeploymentProfiles] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(true)
  const getRegionDisplayName = useCallback((row, value) => {
    return supportedRegionsByKey[APP_ID_LOGGING_SERVICE]?.find((region: { value: any }) => region.value === value)?.description
  }, [supportedRegionsByKey])
  const columns = useMemo(() => {
    return [{
      Header: 'Profile Name',
      accessor: 'profile_name',
      width: 195,
      Cell: (props: any) => <DeploymentProfileNameRenderer row={props.row} />
    },
    {
      Header: 'Subscriptions',
      accessor: 'subscriptions',
      Cell: (props: any) => <SubscriptionRenderer value={props.value} />,
      width: 250
    },
    {
      Header: 'Region',
      accessor: 'region',
      Cell: (props: any) => <>{getRegionDisplayName(props.row, props.value)}</>,
      width: 200
    },
    {
      Header: 'Support Account',
      accessor: 'support_account_id',
      Cell: (props: any) => {
        const { row } = props
        const support_account = user?.supportAccountIds?.find((s: any) => s.accountid === row.values?.support_account_id)
        return support_account?.name ? `${row.values.support_account_id} - ${support_account?.name}` : row.values.support_account_id
      },
      width: 220
    },
    {
      Header: 'Profile Association Status',
      accessor: 'status',
      Cell: (props: any) => <>{getStatus(props.value)}</>,
      width: 220
    },
    {
      Header: 'Last Updated',
      accessor: 'last_updated',
      Cell: (props: any) => getDateFormat(props.value),
      width: 50
    }]
  }, [getRegionDisplayName, user?.supportAccountIds])

  useEffect(() => {
    async function fetchData() {
      //const cspAccounts = auth.user.supportAccountIds
      const { data } = await SubscriptionService.deploymentProfiles.get(selectedTreeNode?.id || globalTsgId)
      // console.log('data', data)
      setDeploymentProfiles(data)
      // setDeploymentProfiles([
      //   {
      //     'profile_name': 'VM-Profile-1',
      //     'region': 'americas',
      //     'tsg_id': '1042582320',
      //     'support_account_id': 6,
      //     'last_updated': '2022-12-15T20:59:23.838Z',
      //     'status': 'complete',
      //     'subscriptions': [
      //       {
      //         'tenant_id': '1758289149386476288',
      //         'support_account_id': '6',
      //         'tsg_id': '1042582320',
      //         'iam_controlled': true,
      //         'csp_tenant_id': '1758289149386476288',
      //         'created_by': 'nparmar@paloaltonetworks.com',
      //         'url': 'https://zbvmflex2.iot.paloaltonetworks.com',
      //         'license_name': 'zingbox',
      //         'tenant_instance_name': 'vmflex-2 - IoT',
      //         'extra': {
      //           'tenant_id': '1758289149386476288',
      //           'pri_panorama': '',
      //           'allocation_type': 'FULL',
      //           'platform_id': '1563292173',
      //           'sec_panorama': '',
      //           'has_msp': true
      //         },
      //         'support_account_name': 'Adesa',
      //         'region': 'americas',
      //         'platform_region': 'americas',
      //         'app_id': 'zingbox',
      //         'provisioning_status': 'error',
      //         'provisioning_message': {},
      //         '_internal': {
      //           'created_at': 1682973770000,
      //           'updated_at': 1682973771000
      //         }
      //       },
      //       {
      //         'tenant_id': '1758289149386476288',
      //         'support_account_id': '6',
      //         'tsg_id': '1042582320',
      //         'iam_controlled': true,
      //         'csp_tenant_id': '1758289149386476288',
      //         'created_by': 'nparmar@paloaltonetworks.com',
      //         'url': 'https://zbvmflex2.iot.paloaltonetworks.com',
      //         'license_name': 'strat_insights',
      //         'tenant_instance_name': 'vmflex-2 - IoT',
      //         'extra': {
      //           'tenant_id': '1758289149386476288',
      //           'pri_panorama': '',
      //           'allocation_type': 'FULL',
      //           'platform_id': '1563292173',
      //           'sec_panorama': '',
      //           'has_msp': true
      //         },
      //         'support_account_name': 'Adesa',
      //         'region': 'americas',
      //         'platform_region': 'americas',
      //         'app_id': 'strata_insights',
      //         'provisioning_status': 'error',
      //         'provisioning_message': {},
      //         '_internal': {
      //           'created_at': 1682973770000,
      //           'updated_at': 1682973771000
      //         }
      //       }
      //     ]
      //   },
      //   {
      //     'profile_name': 'VM-Profile-2',
      //     'region': 'americas',
      //     'tsg_id': '1042582320',
      //     'support_account_id': 6,
      //     'last_updated': '2022-12-15T20:59:23.838Z',
      //     'status': 'complete',
      //     'subscriptions': [
      //       {
      //         'tenant_id': '4993904268080030720',
      //         'associations': [
      //           {
      //             'tenant_id': '1563292173',
      //             'tsg_id': '1042582320',
      //             'csp_tenant_id': '1563292173',
      //             'region': 'americas',
      //             'app_id': 'logging_service'
      //           }
      //         ],
      //         'support_account_id': '6',
      //         'tsg_id': '1042582320',
      //         'iam_controlled': true,
      //         'provisioning_status': 'initializing',
      //         'csp_tenant_id': '4993904268080030720',
      //         'created_by': 'nparmar@paloaltonetworks.com',
      //         'url': 'https://dsdsd.staging.cirrotester.com',
      //         'license_name': 'aperture',
      //         'tenant_instance_name': 'vmflex-2 - Prisma SaaS',
      //         'extra': {
      //           'tenant_id': '4993904268080030720',
      //           'pri_panorama': '',
      //           'allocation_type': 'FULL',
      //           'platform_id': '1563292173',
      //           'sec_panorama': '',
      //           'has_msp': true
      //         },
      //         'support_account_name': 'Adesa',
      //         'region': 'americas',
      //         'platform_region': 'americas',
      //         'app_id': 'aperture',
      //         '_internal': {
      //           'updated_at': 1682976741000,
      //           'created_at': 1682976741000
      //         }
      //       }
      //     ]
      //   }
      // ])
      setLoading(false)
    }
    fetchData()
  }, [auth.user.email, auth.user.supportAccountIds, selectedTreeNode, globalTsgId])
  return (
    <TableHeightFix className='cm-h-full cm-w-full'>
      <TableProvider
        columns={columns}
        data={deploymentProfiles}
        enableColumnSort
        enableResizeColumns
        enablePagination
        isLoading={loading}
      >
        <ConnectedTableLayout
          overflow
          enableHeaderTitle={true}
          // controlLeft={<HeaderTitle title={'Subscriptions'} />}
          sidecar={<div />}
          resultsSummary={<ConnectedResultsSummary />}
          paginator={<ConnectedPaginator pageSizeOptions={[10, 25, 50, 100, 500]} />}
        >
          <ConnectedTable
            rowSize='cozy'
            emptyStateProps={{
              heading: 'No Available Deployment Profiles',
            }}
          />
        </ConnectedTableLayout>
      </TableProvider>
    </TableHeightFix>
  )
}

export default DeploymentProfilesTable
